import { useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router"
import { ButtonDestructive, ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import {
    useDeleteMaintenanceTemplate,
    useMaintenanceTemplate,
    useUpdateMaintenanceTemplate,
} from "features/pilot-tracking/maintenance/MaintenanceTemplateQueries"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { MaintenanceTemplateForm } from "./MaintenanceTemplateForm"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { createQuery } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import {
    loadTemplate,
    selectFormAsCreatable,
    selectSubmitting,
    setSubmitting,
} from "features/pilot-tracking/maintenance/slice"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"

function CancelButton() {
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)
    return (
        <ButtonTertiary
            title="Cancel"
            onClick={() => router.navigate({ path: "/fleet/maintenance" })}
            disabled={submitting}
        />
    )
}

function DeleteTemplateButton() {
    const { templateId } = useParams<{ templateId: string }>()
    const { deleteMaintenanceTemplate } = useDeleteMaintenanceTemplate()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)
    const confirmationModal = useConfirmationModalContext()

    const handleDelete = useCallback(async () => {
        dispatch(setSubmitting(true))
        try {
            await deleteMaintenanceTemplate(templateId!)
            dispatch(
                setGlobalNotificationContext({
                    message: "Template deleted.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            router.navigate({ path: "/fleet/maintenance" })
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to delete the template. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            dispatch(setSubmitting(false))
        }
    }, [templateId, deleteMaintenanceTemplate, dispatch, router])

    return (
        <ButtonDestructive
            title="Delete Template"
            LeftIcon={Icons.Trash}
            onClick={async () => {
                await confirmationModal.confirm({
                    RenderComponent: getGenericConfirmationModalComponent({
                        title: "Delete Template?",
                        message: "Are you sure you want to delete this template?",
                        confirmText: "Delete Template",
                    }),
                    asyncConfirmCallback: handleDelete,
                })
            }}
            disabled={submitting}
        />
    )
}

function UpdateTemplateSubmitButton() {
    const { templateId } = useParams<{ templateId: string }>()
    const form = useAppSelector(selectFormAsCreatable)
    const { isLoading } = useMaintenanceTemplate(templateId!)
    const { updateMaintenanceTemplate } = useUpdateMaintenanceTemplate()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)

    const handleSubmit = useCallback(async () => {
        dispatch(setSubmitting(true))
        try {
            await updateMaintenanceTemplate({ templateId: templateId!, body: form })
            dispatch(
                setGlobalNotificationContext({
                    message: `${form.name} has been saved.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            router.navigate({ path: "/fleet/maintenance" })
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to save ${form.name}. Please try again.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            dispatch(setSubmitting(false))
        }
    }, [form, templateId, updateMaintenanceTemplate, dispatch, router])

    return (
        <ButtonPrimary
            title="Edit Template"
            RightIcon={Icons.Checkmark}
            onClick={handleSubmit}
            disabled={isLoading || submitting}
        />
    )
}

export const UpdateMaintenanceTemplatePage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Edit Maintenance Template",
        rightItems: [DeleteTemplateButton, CancelButton, UpdateTemplateSubmitButton],
        border: true,
    }),
    Content: UpdateMaintenanceTemplatePageContent,
    maxWidth: 1200,
})

function UpdateMaintenanceTemplatePageContent() {
    const { templateId } = useParams<{ templateId: string }>()
    const { data: template, isLoading } = useMaintenanceTemplate(templateId!)
    const dispatch = useAppDispatch()
    const [isFormReady, setIsFormReady] = useState(false)

    const aircraftLinksQuery = useMemo(
        () =>
            createQuery({
                resource: "maintenance_template_aircraft",
                select: ["aircraft_id"],
                filters: [{ field: "template_id", op: "=", value: templateId! }],
            }),
        [templateId]
    )
    const { data: aircraftLinks, isLoading: isAircraftLinksLoading } =
        usePilotTrackingQuery(aircraftLinksQuery)

    useEffect(() => {
        if (template && aircraftLinks && !isFormReady) {
            dispatch(
                loadTemplate({
                    template,
                    aircraftIds: aircraftLinks.map((r) => r.aircraft_id),
                })
            )
            setIsFormReady(true)
        }
    }, [template, aircraftLinks, isFormReady, dispatch])

    if (isLoading || isAircraftLinksLoading || !isFormReady) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    return <MaintenanceTemplateForm />
}
